// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
import { usePermissions } from 'datastage/utils/permissions';
// --- Hooks
import { useRightsData } from '../../../hooks/useRightsData';

/**
 * Custom hook for rights grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the rights grid
 * - Fetches sorted rights data from the API via useRightsData
 * - Prepares column definitions with sorting controls based on user permissions
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useRightsGridUtils({ columnHandlers = [] } = {}) {
    const { fetchRights, updateRights, rightRange } = useRightsData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const { isRightsetsAssociateUser } = usePermissions();

    // Handle sorting: update state, fetch sorted data, update rights
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchRights(rightRange, null, customSort);

            if (data) {
                updateRights({
                    rights: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    rightSort: customSort,
                });
            }
        },
        [fetchRights, updateRights, rightRange],
    );

    // Prepare column definitions with sorting controls based on user permissions
    const baseColumns = isRightsetsAssociateUser
        ? columnDefs['rights'](...columnHandlers)['default']
        : columnDefs['rights'](...columnHandlers)['limited'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
